'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';


interface Testimonial {
  id: string;
  name: string;
  location: string;
  rating: number;
  comment: string;
  image: string;
  alt: string;
  verified: boolean;
}

const testimonials: Testimonial[] = [
{
  id: '1',
  name: 'Kari Larsen',
  location: 'Oslo',
  rating: 5,
  comment: 'Fantastisk opplevelse! Jeg var skeptisk til å selge gullet mitt online, men Gullklar gjorde prosessen så enkel og trygg. Fikk bedre pris enn jeg forventet, og pengene var på konto neste dag.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10e6af713-1763296487310.png",
  alt: 'Professional headshot of middle-aged Norwegian woman with blonde hair wearing navy blazer smiling at camera',
  verified: true
},
{
  id: '2',
  name: 'Lars Hansen',
  location: 'Bergen',
  rating: 5,
  comment: 'Jeg hadde arvet gullsmykker som jeg ikke brukte. Gullklar ga meg et rettferdig tilbud og forklarte alt tydelig. Ingen skjulte kostnader, og de holdt det de lovet. Anbefales på det sterkeste!',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4010ec0-1763296756417.png",
  alt: 'Professional portrait of Norwegian man in his fifties with gray hair wearing white shirt in office setting',
  verified: true
},
{
  id: '3',
  name: 'Ingrid Olsen',
  location: 'Trondheim',
  rating: 5,
  comment: 'Veldig profesjonell service. De svarte raskt på alle mine spørsmål, og jeg følte meg trygg gjennom hele prosessen. Fikk mer enn jeg hadde fått hos lokale gullkjøpere.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19dce1441-1763301205792.png",
  alt: 'Smiling Norwegian woman in her forties with brown hair wearing casual sweater outdoors in natural lighting',
  verified: true
},
{
  id: '4',
  name: 'Erik Johansen',
  location: 'Stavanger',
  rating: 5,
  comment: 'Jeg solgte gamle gullmynter og fikk en utmerket pris. Hele prosessen tok bare noen dager, og kommunikasjonen var førsteklasses. Vil definitivt bruke Gullklar igjen.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_189faedcb-1763296408679.png",
  alt: 'Professional photo of Norwegian businessman in his thirties with dark hair wearing suit and tie',
  verified: true
}];


const TestimonialSection = () => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isHydrated, setIsHydrated] = useState<boolean>(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  if (!isHydrated) {
    return (
      <section className="bg-background py-12 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="font-headline text-3xl font-bold text-foreground lg:text-4xl">
              Hva våre kunder sier
            </h2>
            <p className="mt-4 font-body text-lg text-muted-foreground lg:text-xl">
              Vi legger stor vekt på personlig oppfølging og tydelig kommunikasjon gjennom hele prosessen.
            </p>
          </div>
          <div className="relative mx-auto max-w-4xl">
            <div className="rounded-lg bg-card p-8 shadow-xl lg:p-12">
              <div className="mb-6 flex items-center space-x-4">
                <div>
                  <div className="font-headline text-lg font-semibold text-foreground">
                    {testimonials[0].name}
                  </div>
                  <div className="font-body text-sm text-muted-foreground">
                    {testimonials[0].location}
                  </div>
                </div>
              </div>
              <div className="mb-4 flex space-x-1">
                {[...Array(5)].map((_, i) =>
                <Icon key={i} name="StarIcon" size={20} variant="solid" className="text-accent" />
                )}
              </div>
              <p className="font-body text-base text-foreground lg:text-lg">
                "{testimonials[0].comment}"
              </p>
            </div>
          </div>
        </div>
      </section>);

  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="bg-background py-12 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mb-12 text-center">
          <h2 className="font-headline text-3xl font-bold text-foreground lg:text-4xl">
            Vår tilnærming
          </h2>
          <p className="mt-4 font-body text-lg text-muted-foreground lg:text-xl">
            Vi legger stor vekt på tydelig kommunikasjon og en ryddig prosess, slik at du alltid vet hva du forholder deg til.
          </p>
        </div>

        <div className="relative mx-auto max-w-4xl">
          <div className="rounded-lg bg-card p-8 shadow-xl lg:p-12">
            <div className="mb-6 flex items-center space-x-4">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <div className="font-headline text-lg font-semibold text-foreground">
                    {currentTestimonial.name}
                  </div>
                  {currentTestimonial.verified &&
                  <Icon name="CheckBadgeIcon" size={20} variant="solid" className="text-success" />
                  }
                </div>
                <div className="font-body text-sm text-muted-foreground">
                  {currentTestimonial.location}
                </div>
              </div>
            </div>

            <div className="mb-4 flex space-x-1">
              {[...Array(currentTestimonial.rating)].map((_, i) =>
              <Icon key={i} name="StarIcon" size={20} variant="solid" className="text-accent" />
              )}
            </div>

            <p className="font-body text-base text-foreground lg:text-lg">
              "{currentTestimonial.comment}"
            </p>
          </div>

          <button
            onClick={handlePrevious}
            className="absolute left-0 top-1/2 -translate-x-12 -translate-y-1/2 rounded-full bg-card p-3 shadow-md transition-all duration-250 hover:bg-primary hover:text-primary-foreground"
            aria-label="Previous testimonial">

            <Icon name="ChevronLeftIcon" size={24} variant="outline" />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 rounded-full bg-card p-3 shadow-md transition-all duration-250 hover:bg-primary hover:text-primary-foreground"
            aria-label="Next testimonial">

            <Icon name="ChevronRightIcon" size={24} variant="outline" />
          </button>

          <div className="mt-6 flex justify-center space-x-2">
            {testimonials.map((_, index) =>
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 w-2 rounded-full transition-all duration-250 ${
              index === currentIndex ? 'w-8 bg-primary' : 'bg-border'}`
              }
              aria-label={`Go to testimonial ${index + 1}`} />

            )}
          </div>
        </div>

        <div className="mt-12 text-center">
          




        </div>
      </div>
    </section>);

};

export default TestimonialSection;